import Vue from 'vue'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import relativeTime from 'dayjs/plugin/relativeTime'
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import store from './vuex'
import { createTimezoneHelpers } from './timezone.mjs'

dayjs.extend(weekOfYear)
dayjs.extend(relativeTime)
dayjs.extend(isToday)
dayjs.extend(isYesterday)
dayjs.extend(utc)
dayjs.extend(timezone)

// Fallback only. The live value is served by the API at login (see
// getBaseline); this covers the first paint before any login response, and any
// deployment whose backend predates that field.
// Europe/Dublin observes DST, so this is a ZONE, never a fixed offset.
export const BASELINE_TIMEZONE =
    process.env.VUE_APP_BASELINE_TIMEZONE || 'Europe/Dublin'

// The company wall-clock reference. Read from the store on every call so a
// login response updates it without a rebuild, falling back to the build-time
// value when the API has not supplied one.
const getBaseline = () =>
    (store.state.settings && store.state.settings.baselineTimezone) || BASELINE_TIMEZONE

// Reading store state here (rather than caching it) is what makes every
// $date() call site reactive: Vue tracks the read during render.
const getDisplayZone = () => (store.state.settings && store.state.settings.timezone) || null

const helpers = createTimezoneHelpers(dayjs, {
    baseline: getBaseline,
    getDisplayZone,
})

// Display entry point. Carries the whole dayjs surface plus its statics so
// existing calls like $date.tz.guess() keep working.
const $date = (value) => helpers.toDisplay(value)
Object.assign($date, dayjs)

// Write entry point: use for EVERY value that goes into an API payload.
const $dateApi = (value) => helpers.toBaseline(value)

const offsetLabel = (zone) => {
    try {
        return `GMT${dayjs().tz(zone).format('Z').replace(':00', '').replace(/^([+-])0/, '$1')}`
    } catch (_) {
        return ''
    }
}

// "12" or "24". Read from the store on every access so that, like $date(),
// anything using it re-renders when the preference changes.
const getTimeFormat = () =>
    (store.state.settings && store.state.settings.timeFormat) === '24' ? '24' : '12'

const $tz = {
    get baseline() {
        return getBaseline()
    },
    display: () => getDisplayZone() || getBaseline(),
    offsetLabel,
    splitLocalDateTime: helpers.splitLocalDateTime,
    joinToBaseline: helpers.joinToBaseline,

    // The dayjs time token for the user's preferred clock. Compose it into a
    // format string: $date(x).format(`MMM D, ${$tz.time}`)
    get time() {
        return getTimeFormat() === '24' ? 'HH:mm' : 'h:mm A'
    },
    // True when the user wants a 12-hour clock. Vuetify's v-time-picker wants
    // 'ampm' | '24hr'; FullCalendar wants a boolean hour12.
    get ampm() {
        return getTimeFormat() === '12'
    },
    get pickerFormat() {
        return getTimeFormat() === '24' ? '24hr' : 'ampm'
    },
    // Renders a stored 24-hour "HH:mm" wall-clock string in the user's
    // preferred clock, without any zone conversion. For values that are
    // already in the right zone, such as the meeting date/time pair.
    showTime(hhmm) {
        if (!hhmm) return ''
        if (getTimeFormat() === '24') return hhmm
        const [h, m] = String(hhmm).split(':')
        const hour = Number(h)
        if (Number.isNaN(hour)) return hhmm
        const suffix = hour < 12 ? 'AM' : 'PM'
        const twelve = hour % 12 === 0 ? 12 : hour % 12
        return `${twelve}:${m} ${suffix}`
    },
}

Object.defineProperties(Vue.prototype, {
    $date: { get() { return $date } },
    $dateApi: { get() { return $dateApi } },
    $tz: { get() { return $tz } },
})

export { $date, $dateApi, $tz }
